/**
 * AssistantComposer — the question box (mission 8 WP08 T042; FR-012, FR-013, FR-025; OR-9, OR-13;
 * amendment 14; squad U3, U13, U15; post-tasks P15; sheet 22 callouts 3, 8 and 9).
 *
 * - **Length (OR-13, U13).** No `maxLength`: a paste is never cut. The count is in runes
 *   (`[...text].length`, so an emoji is one), shown as `assistantComposerCount`; over
 *   `QUESTION_MAX_RUNES` the box is `aria-invalid`, `assistantQuestionTooLong` says why and Send is
 *   disabled with that reason. An empty or blank question disables Send with `assistantQuestionRequired`.
 * - **Keys.** Enter is a newline; Ctrl+Enter or Cmd+Enter sends.
 * - **The draft** lives in the panel store (`draft`, `setDraft`), so it survives a close within the
 *   page (U3, U21.3). It is cleared after a successful send and kept on a refusal (amendment 14, U3).
 * - **Prefill (OR-9, U15).** Read whenever the store's `prefill` changes, never only on mount: the
 *   provider consumes a cross-route prefill in its own effect, which runs after this child's. The
 *   question is built here from the register by kind (typed fields only, S19). It fills an empty
 *   composer; over a draft it is offered as a suggested prompt.
 * - **Suggested prompts** (22/8) while the conversation is empty: triage (hidden at 0 WARN
 *   findings), data or mapping, and the column question. A click fills the composer and focuses it;
 *   it never sends (P15).
 * - **Disabled** by the panel with a stated reason (the run's data is gone, the run is not ready, no
 *   credit cycle): `disabledReasonId` names the element that says why.
 * - **Send waits** (mission 8 review UI1) while `sendWaitsFor` names an element (the log's loading caption
 *   while page 1 loads, or its read-failed notice): Send is disabled and described by it; the box and the
 *   prompts stay usable, so a draft is kept.
 * - **Refused by billing** (mission 9 WP14 T090; amendment 8; FR-015; B.1; U11): while `useBillingGate`
 *   refuses, the box, the prompts and Send are disabled and described by a caption with the gate's
 *   reason. The panel's own reason wins when both apply, and the panel's throttled notice replaces the
 *   composer altogether (mission 8, TR-1), so the read-only reason never masks it.
 */
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState, type KeyboardEvent } from 'react'
import { SendHorizontal } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useAssistantPanel, type AssistantPrefill } from '@/lib/assistant-panel'
import { useBillingGate } from '@/lib/billing-gate'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

/** The longest question (C-010): `MaxQuestionRunes` in backend/internal/domain/assistant/message.go. */
export const QUESTION_MAX_RUNES = 2000

/** The question an entry point's prefill asks, from the register (OR-9; FR-013). */
export function prefillQuestion(prefill: AssistantPrefill): string {
  switch (prefill.kind) {
    case 'rule':
      return PROVISIONAL_COPY.assistantPrefillRule.render({ ruleId: prefill.ruleId, count: prefill.count })
    case 'column':
      return PROVISIONAL_COPY.assistantPrefillColumn.render({ column: prefill.column, count: prefill.count })
    case 'findings':
      return PROVISIONAL_COPY.assistantPrefillFindings.render({ count: prefill.count })
  }
}

export interface AssistantComposerProps {
  /** Ask the question; resolves on an answer, rejects on a refusal (the panel renders the refusal). */
  onSend: (question: string) => Promise<unknown>
  /** The ask is in flight. */
  sending: boolean
  /** The conversation is empty: offer the suggested prompts (22/8). */
  showPrompts: boolean
  /** The run's WARN findings, for the triage prompt (hidden at 0). */
  warnCount: number
  /** The id of the element stating why the composer is disabled. */
  disabledReasonId?: string | undefined
  /** The id of the element Send waits for: set, Send alone is disabled (mission 8 review UI1). */
  sendWaitsFor?: string | undefined
}

const PROMPT_CLASS = 'h-auto min-h-8 whitespace-normal py-1.5 text-left'

export const AssistantComposer = forwardRef<HTMLTextAreaElement, AssistantComposerProps>(function AssistantComposer(
  { onSend, sending, showPrompts, warnCount, disabledReasonId, sendWaitsFor },
  ref,
) {
  const { draft, setDraft, prefill, consumePrefill } = useAssistantPanel()
  const [offered, setOffered] = useState<string | null>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  useImperativeHandle(ref, () => box.current as HTMLTextAreaElement, [])
  const id = useId()
  const countId = `${id}-count`
  const errorId = `${id}-error`
  const billingId = `${id}-billing`
  const gate = useBillingGate()

  useEffect(() => {
    if (prefill === null) return
    const taken = consumePrefill()
    if (taken === null) return
    const question = prefillQuestion(taken)
    if (draft.trim() === '') {
      setDraft(question)
      setOffered(null)
    } else {
      setOffered(question)
    }
  }, [prefill, draft, consumePrefill, setDraft])

  const runes = [...draft].length
  const tooLong = runes > QUESTION_MAX_RUNES
  const empty = draft.trim() === ''
  const tooLongText = PROVISIONAL_COPY.assistantQuestionTooLong.render({ max: QUESTION_MAX_RUNES })
  // The panel's reason first, then the billing gate's (T090).
  const blockedBy = disabledReasonId ?? (gate.refused ? billingId : undefined)
  const blocked = blockedBy !== undefined
  const invalid = empty ? copyText('assistantQuestionRequired') : tooLong ? tooLongText : null
  const waiting = sendWaitsFor !== undefined
  const canSend = !blocked && !waiting && !sending && invalid === null

  function submit() {
    if (!canSend) return
    onSend(draft).then(
      () => setDraft(''),
      () => undefined,
    )
  }

  function fill(question: string) {
    setDraft(question)
    setOffered(null)
    box.current?.focus()
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      submit()
    }
  }

  const triage = PROVISIONAL_COPY.assistantPromptTriage.render({ count: warnCount })
  const prompts = [
    ...(offered !== null ? [offered] : []),
    ...(showPrompts
      ? [triage, copyText('assistantPromptDataOrMapping'), copyText('assistantPromptColumn')].filter((p) => p !== '')
      : []),
  ]

  const sendState = blocked
    ? ({ disabled: true, 'aria-describedby': blockedBy } as const)
    : waiting
      ? ({ disabled: true, 'aria-describedby': sendWaitsFor } as const)
      : invalid !== null
        ? ({ disabled: true, disabledReason: invalid } as const)
        : ({} as const)

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      {prompts.length > 0 ? (
        <ul data-prompts="" className="flex flex-col items-start gap-2">
          {prompts.map((prompt) => (
            <li key={prompt}>
              <Button variant="secondary" size="sm" className={PROMPT_CLASS} disabled={blocked} aria-describedby={blockedBy} onClick={() => fill(prompt)}>
                {prompt}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {disabledReasonId === undefined && gate.refused ? (
        <p id={billingId} data-composer-billing-reason="" className="text-caption text-navy-600">
          {gate.reason}
        </p>
      ) : null}
      <Label htmlFor={id}>{copyText('assistantComposerLabel')}</Label>
      <div className="flex items-end gap-2">
        <Textarea
          id={id}
          ref={box}
          rows={3}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          readOnly={sending}
          disabled={blocked}
          aria-invalid={tooLong ? true : undefined}
          aria-describedby={[tooLong ? errorId : null, countId, blockedBy ?? null].filter(Boolean).join(' ')}
          className="min-h-20 flex-1 resize-y"
        />
        <Button
          type="submit"
          size="icon"
          aria-label={copyText('assistantSend')}
          icon={<SendHorizontal aria-hidden />}
          loading={sending}
          {...sendState}
        />
      </div>
      <div className="flex items-start justify-between gap-3">
        {tooLong ? (
          <p id={errorId} className="text-caption text-[var(--color-error-700)]">
            {tooLongText}
          </p>
        ) : (
          <span />
        )}
        <p id={countId} className="shrink-0 text-caption text-navy-600">
          {PROVISIONAL_COPY.assistantComposerCount.render({ length: runes, max: QUESTION_MAX_RUNES })}
        </p>
      </div>
    </form>
  )
})
